// useBaseSeatLimit.js

import { useEffect, useMemo, useState } from 'react';

import { getPlanByKey } from 'services/plans/PlanService';
import { resolveMemberCapFromPlan } from 'services/bases/BaseEntitlementsService';
import { listBaseInvites } from 'services/bases/BaseInviteService';

function resolveMemberCount({ memberCount, baseMemberCount }) {
    const subscriptionCount = Number(memberCount);
    if (Number.isFinite(subscriptionCount) && subscriptionCount > 0) {
        return Math.floor(subscriptionCount);
    }

    const baseCount = Number(baseMemberCount);
    if (Number.isFinite(baseCount) && baseCount > 0) {
        return Math.floor(baseCount);
    }

    return 0;
}

/**
 * useBaseSeatLimit
 *
 * Resolves base seat capacity from the active plan and current membership.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.isActive - Whether seat-limit state should be loaded.
 * @param {number} params.memberCount - Current member count from live membership data.
 * @param {string} params.baseKey - Active base key.
 * @param {string} params.planKey - Active base plan key.
 * @param {number} [params.baseMemberCount] - Base model member count fallback.
 * @param {boolean} [params.canListPendingInvites] - Whether pending invites can be loaded.
 *
 * @returns {object} Seat-limit loading state and derived capacity values.
 */
const useBaseSeatLimit = ({
    isActive,
    memberCount,
    baseKey,
    planKey,
    baseMemberCount = 0,
    canListPendingInvites = false,
}) => {
    const [currentPlan, setCurrentPlan] = useState(null);
    const [planLoading, setPlanLoading] = useState(false);
    const [pendingInviteCount, setPendingInviteCount] = useState(0);
    const [invitesLoading, setInvitesLoading] = useState(false);

    useEffect(() => {
        const cleanedPlanKey = String(planKey || '').trim();
        if (!isActive || !cleanedPlanKey) {
            setCurrentPlan(null);
            setPlanLoading(false);
            return;
        }

        let isMounted = true;
        setPlanLoading(true);

        getPlanByKey({ planKey: cleanedPlanKey })
            .then((planRow) => {
                if (!isMounted) return;
                setCurrentPlan(planRow || null);
            })
            .catch(() => {
                if (!isMounted) return;
                setCurrentPlan(null);
            })
            .finally(() => {
                if (!isMounted) return;
                setPlanLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [isActive, planKey]);

    useEffect(() => {
        const cleanedBaseKey = String(baseKey || '').trim();
        if (!isActive || !cleanedBaseKey || !canListPendingInvites) {
            setPendingInviteCount(0);
            setInvitesLoading(false);
            return;
        }

        let isMounted = true;
        setInvitesLoading(true);

        listBaseInvites({ baseKey: cleanedBaseKey })
            .then((rows) => {
                if (!isMounted) return;
                setPendingInviteCount(Array.isArray(rows) ? rows.length : 0);
            })
            .catch(() => {
                if (!isMounted) return;
                setPendingInviteCount(0);
            })
            .finally(() => {
                if (!isMounted) return;
                setInvitesLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, [canListPendingInvites, isActive, baseKey]);

    const resolvedMemberCount = useMemo(() => {
        return resolveMemberCount({
            memberCount,
            baseMemberCount,
        });
    }, [memberCount, baseMemberCount]);

    const seatLimit = useMemo(() => {
        return resolveMemberCapFromPlan(currentPlan);
    }, [currentPlan]);

    const hasSeatLimit = seatLimit != null;

    const reservedSeatCount = useMemo(() => {
        return Math.max(0, resolvedMemberCount) + Math.max(0, pendingInviteCount);
    }, [pendingInviteCount, resolvedMemberCount]);

    const isAtSeatLimit = useMemo(() => {
        if (!hasSeatLimit) return false;
        return reservedSeatCount >= seatLimit;
    }, [hasSeatLimit, reservedSeatCount, seatLimit]);

    const membersSubtitle = useMemo(() => {
        if (planLoading || invitesLoading) {
            return 'Loading members...';
        }

        if (!hasSeatLimit) {
            const label = `${resolvedMemberCount} member${resolvedMemberCount === 1 ? '' : 's'}`;
            if (pendingInviteCount > 0) {
                return `${label} • ${pendingInviteCount} pending invite${pendingInviteCount === 1 ? '' : 's'}`;
            }
            return label;
        }

        const usageLabel = `${reservedSeatCount} / ${seatLimit}`;
        const memberLabel = `${resolvedMemberCount} member${resolvedMemberCount === 1 ? '' : 's'}`;
        if (pendingInviteCount > 0) {
            return `${usageLabel} seats • ${memberLabel} • ${pendingInviteCount} pending invite${pendingInviteCount === 1 ? '' : 's'}`;
        }
        return `${usageLabel} members • ${memberLabel}`;
    }, [
        hasSeatLimit,
        invitesLoading,
        pendingInviteCount,
        planLoading,
        reservedSeatCount,
        resolvedMemberCount,
        seatLimit,
    ]);

    const seatLimitDescription = useMemo(() => {
        const planTitle = String(currentPlan?.title || '').trim() || 'the current plan';
        if (!hasSeatLimit) return '';

        return `This space has reached the ${seatLimit}-member limit for ${planTitle}.`;
    }, [currentPlan?.title, hasSeatLimit, seatLimit]);

    return {
        currentPlan,
        hasSeatLimit,
        invitesLoading,
        isAtSeatLimit,
        loading: planLoading || invitesLoading,
        memberCount: resolvedMemberCount,
        membersSubtitle,
        pendingInviteCount,
        planLoading,
        reservedSeatCount,
        seatLimit,
        seatLimitDescription,
    };
};

export default useBaseSeatLimit;
